import React, { useContext, useEffect, useMemo, useState } from 'react'
import { getUserLocation } from '../Components/CommonComponent'
import { Button } from 'primereact/button'
import { useTranslation } from 'react-i18next'
import ReligionsDropdown from '../Components/ReligionsDropdown'
import { Controller, useForm } from 'react-hook-form'
import { listAmenityCatalogService } from '../Service/AmenityService'
import { listByFiltersReligiousPlaceService } from '../Service/ReligiousPlaceService'
import { MultiSelect } from 'primereact/multiselect'
import { amenityDisplayName } from '../Utils/amenityDisplayName'
import { InputText } from 'primereact/inputtext'
import i18n from '../multilingual/config'
import ReactGoogleAutocomplete from 'react-google-autocomplete'
import { ButtonGroup } from 'primereact/buttongroup';
import GoogleMap from '../Components/MapComponent'
import PaginatorComponent from '../Components/PaginatorComponent'
import { Dialog } from 'primereact/dialog'
import ReligiousPlaceDetailsComponent from '../Components/ReligiousPlaceDetailsComponent'
import AppBodyComponent from '../Components/AppBodyComponent'
import AuthContext from '../Contexts/AuthContext'
import moment from 'moment'

function PlaceList() {
    const { t } = useTranslation('register_place');
    const { setActiveMenu } = useContext(AuthContext)
    const [page, setPage] = useState(1)
    useEffect(() => {
        setActiveMenu(2)
    }, [])

    const [total, setTotal] = useState(1)
    const [placeDetailsAccessKey, setPlaceDetailsAccessKey] = useState()
    const [isDialogVisible, setIsDialogVisible] = useState(false)
    const [lat, setLat] = useState(null)
    const [lng, setLng] = useState(null)
    const [distance, setDistance] = useState(i18n.language === 'en' ? 50 : 10) //50 miles or 10 km
    const [kmMiles, setKmMiles] = useState('km')
    const [address, setAddress] = useState('')
    const [name, setName] = useState('')
    const [places, setPlaces] = useState()
    const [amenityCatalog, setAmenityCatalog] = useState([])
    const [selectedAmenityIds, setSelectedAmenityIds] = useState([])
    const [isMapVisible, setIsMapVisible] = useState(false)
    const [ceremonyWeekdayMin, setCeremonyWeekdayMin] = useState('')
    const [ceremonyWeekdayMax, setCeremonyWeekdayMax] = useState('')
    const [ceremonyStartMin, setCeremonyStartMin] = useState('')
    const [ceremonyStartMax, setCeremonyStartMax] = useState('')
    const [filterDialogVisible, setFilterDialogVisible] = useState(false)
    const {
        getValues,
        control,
        formState: { errors },
    } = useForm();

    const weekdaySelectOptions = useMemo(() => {
        const loc = i18n.language === 'pt' ? 'pt-br' : i18n.language === 'es' ? 'es' : 'en'
        moment.locale(loc)
        return [{ label: t('place_list_weekday_any'), value: '' }].concat(
            [1, 2, 3, 4, 5, 6, 7].map((i) => ({
                label: moment().day(i - 1).format('ddd'),
                value: String(i),
            }))
        )
    }, [i18n.language, t])

    useEffect(()=>{
        getUserLocation(
            (position) => {setLat(position?.lat); setLng(position?.lng)}, 
            (error) => {setLat(null); setLng(null)}
        )
    },[])

    useEffect(() => {
        listAmenityCatalogService().then((cat) => {
            if (Array.isArray(cat)) setAmenityCatalog(cat)
        })
    }, [])

    const amenityOptions = useMemo(
        () =>
            amenityCatalog.map((a) => ({
                label: amenityDisplayName(a, i18n.language),
                value: a.id,
            })),
        [amenityCatalog, i18n.language]
    )

    const ceremonyFilterPayload = () => {
        let wdMin = ceremonyWeekdayMin === '' ? undefined : Number(ceremonyWeekdayMin)
        let wdMax = ceremonyWeekdayMax === '' ? undefined : Number(ceremonyWeekdayMax)
        if (wdMin !== undefined && Number.isNaN(wdMin)) wdMin = undefined
        if (wdMax !== undefined && Number.isNaN(wdMax)) wdMax = undefined
        if (wdMin !== undefined && wdMax === undefined) wdMax = wdMin
        if (wdMax !== undefined && wdMin === undefined) wdMin = wdMax

        let stMin = ceremonyStartMin?.trim() || undefined
        let stMax = ceremonyStartMax?.trim() || undefined
        if (stMin && !stMax) stMax = stMin
        if (stMax && !stMin) stMin = stMax

        return {
            ceremony_weekday_min: wdMin,
            ceremony_weekday_max: wdMax,
            ceremony_start_min: stMin,
            ceremony_start_max: stMax,
        }
    }

    const runSearch = (pageNum) => {
        if (!lat || !lng || !distance) return
        listByFiltersReligiousPlaceService({
            name: name,
            lat: lat,
            lng: lng,
            religion: getValues('religion') ? getValues('religion') : null,
            distance: kmMiles === 'miles' ? distance : distance * 0.621371,
            page: pageNum,
            amenity_ids: selectedAmenityIds,
            ...ceremonyFilterPayload(),
        })
            .then((res) => {
                setPlaces(res.rows)
                setTotal(res.pages)
            })
            .catch((error) => {
                console.log(error)
            })
    }

    const handleApplyFilters = () => {
        setFilterDialogVisible(false)
        if (page === 1) {
            runSearch(1)
        } else {
            setPage(1)
        }
    }

    useEffect(() => {
        runSearch(page)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- pagination only; filters applied explicitly
    }, [page])

    const googleMapsOptions = {
        types: ["address"],
        fields: ["address_components", "geometry", "place_id", "formatted_address"],
        strictBounds: false,
    }
    
    const handleAddress = (e) =>{
        const lat = e.geometry?.location?.lat();
        setLat(lat)
        const lng = e.geometry?.location?.lng();
        setLng(lng);
        setAddress(e.formatted_address);
    }

    const handleOpenPlaceDetails = (access_key) =>{
        setPlaceDetailsAccessKey(access_key)
        setIsDialogVisible(true)
    }

    const filterDialogFooter = (
        <div className='flex justify-content-end gap-2 flex-wrap'>
            <Button label={t('place_list_filters_cancel')} severity='secondary' outlined onClick={() => setFilterDialogVisible(false)} />
            <Button
                label={t('place_list_filters_apply')}
                icon='pi pi-check'
                onClick={handleApplyFilters}
                disabled={!lat || !lng || !distance}
            />
        </div>
    )

    return (
        <AppBodyComponent>
        <div className='flex flex-column gap-4'>
            <div className='flex flex-column gap-4 p-4'>
                <div className='flex flex-column gap-2'>
                    <div className='flex justify-content-between align-items-center flex-wrap gap-2'>
                        <div className='text-lg font-bold'>{t('place_list_places_finds')}</div>
                        <div className='flex align-items-center gap-2 flex-wrap'>
                            <ButtonGroup>
                                <Button text={isMapVisible ? true : false} icon="pi pi-list" onClick={()=>setIsMapVisible(!isMapVisible)} />
                                <Button text={!isMapVisible ? true : false} icon="pi pi-map" onClick={()=>setIsMapVisible(!isMapVisible)} />
                            </ButtonGroup>
                        </div>
                    </div>
                    <div className='flex justify-content-between align-items-center flex-wrap gap-2'>
                        <div className='flex flex-column gap-1 flex-1'>
                            <label htmlFor='place-list-filter-address'>{t('place_list_address')}</label>
                            <ReactGoogleAutocomplete
                                key={filterDialogVisible ? 'filter-autocomplete-open' : 'filter-autocomplete-closed'}
                                defaultValue={address}
                                className='w-full p-inputtext p-component'
                                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                onPlaceSelected={handleAddress}
                                options={googleMapsOptions}
                            />
                            {(!lat || !lng) && (
                                <span className='form-validation-message'>{t('place_list_inform_address_error')}</span>
                            )}
                        </div>
                        <Button type='button' icon='pi pi-filter' label={t('place_list_filters_button')} onClick={() => setFilterDialogVisible(true)} />
                    </div>
                    {places ? !isMapVisible ? 
                    <div className='flex flex-column gap-2'>
                        {places.map((item, key)=>(
                            <div onClick={()=>handleOpenPlaceDetails(item.access_key)} key={key} className={`flex justify-content-between gap-2 p-2 border-round ${key%2 ? 'bg-gray-200' : ''} cursor-pointer`}>
                                <div className='flex flex-1 flex-column'>
                                    <div className='text-sm font-bold'>{t('place_list_list_name')}</div>
                                    <div>{item.name}</div>
                                </div>
                                <div className='flex flex-1 flex-column'>
                                    <div className='text-sm font-bold'>{t('place_list_list_religion')}</div>
                                    {i18n.language === 'en' ? item.religion_name_en : i18n.language === 'es' ? item.religion_name_es : item.religion_name_pt_br}
                                </div>
                                <div className='flex flex-column'>
                                    <div className='text-sm font-bold'>{t('place_list_list_distance')}</div>
                                    {kmMiles === 'miles' ? (Math.round(Number(item.distance_miles)) + ' miles')  : (Math.round(Number(item.distance_miles) / 0.621371) + ' km')}
                                </div>
                            </div>
                        ))}
                        <PaginatorComponent page={page} total={total} setPage={setPage} />
                    </div> : 
                    <div>
                        <GoogleMap locations={places} />
                    </div> : <div className='font-lg'>{t('place_list_no_places_find')}</div>}
                </div>

            </div>

            <Dialog
                visible={filterDialogVisible}
                header={t('place_list_filters_title')}
                className='w-11 md:w-9 lg:w-6'
                onHide={() => setFilterDialogVisible(false)}
                footer={filterDialogFooter}
                draggable={false}
                dismissableMask
            >
                <div className='flex flex-column gap-4 pt-2'>
                    <div className='flex flex-column gap-1'>
                        <label htmlFor='place-list-filter-name'>{t('place_list_name')}</label>
                        <InputText id='place-list-filter-name' value={name} className='w-full p-inputtext p-component' onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className='flex flex-column gap-1'>
                        <label>{t('place_list_religion')}</label>
                        <Controller
                            name='religion'
                            control={control}
                            render={({ field }) => <ReligionsDropdown field={field} errors={errors} />}
                        />
                    </div>
                    <div className='flex flex-column gap-1'>
                        <label htmlFor='place-list-filter-distance'>
                            {t('place_list_distance')} {`(${kmMiles})`}
                        </label>
                        <InputText
                            id='place-list-filter-distance'
                            type='number'
                            className='w-full md:w-10rem p-inputtext p-component'
                            value={distance}
                            onChange={(e) => setDistance(e.target.value)}
                        />
                        {!distance && <span className='form-validation-message'>{t('place_list_inform_address_error')}</span>}
                    </div>
                    <div className='flex flex-column gap-1 w-full'>
                        <label>{t('place_list_amenities')}</label>
                        <MultiSelect
                            className='w-full'
                            value={selectedAmenityIds}
                            options={amenityOptions}
                            onChange={(e) => setSelectedAmenityIds(e.value || [])}
                            optionLabel='label'
                            optionValue='value'
                            display='chip'
                            filter
                            showClear
                            placeholder={t('place_list_amenities_placeholder')}
                        />
                        <span className='text-sm text-600'>{t('place_list_amenities_hint')}</span>
                    </div>
                    <div className='flex flex-column md:flex-row gap-3 flex-wrap'>
                        <div className='flex flex-column gap-1 flex-1'>
                            <label>{t('place_list_ceremony_weekday_from')}</label>
                            <select
                                className='w-full p-inputtext p-component'
                                value={ceremonyWeekdayMin}
                                onChange={(e) => setCeremonyWeekdayMin(e.target.value)}
                            >
                                {weekdaySelectOptions.map((o) => (
                                    <option key={`wf-${o.value}`} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className='flex flex-column gap-1 flex-1'>
                            <label>{t('place_list_ceremony_weekday_to')}</label>
                            <select
                                className='w-full p-inputtext p-component'
                                value={ceremonyWeekdayMax}
                                onChange={(e) => setCeremonyWeekdayMax(e.target.value)}
                            >
                                {weekdaySelectOptions.map((o) => (
                                    <option key={`wt-${o.value}`} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className='flex flex-column gap-1 flex-1'>
                            <label>{t('place_list_ceremony_start_from')}</label>
                            <input type='time' className='p-inputtext p-component w-full' value={ceremonyStartMin} onChange={(e) => setCeremonyStartMin(e.target.value)} />
                        </div>
                        <div className='flex flex-column gap-1 flex-1'>
                            <label>{t('place_list_ceremony_start_to')}</label>
                            <input type='time' className='p-inputtext p-component w-full' value={ceremonyStartMax} onChange={(e) => setCeremonyStartMax(e.target.value)} />
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog visible={isDialogVisible} header maximized onHide={()=>setIsDialogVisible(false)}>
                <ReligiousPlaceDetailsComponent access_key={placeDetailsAccessKey} />
            </Dialog>

            
        </div>
        </AppBodyComponent>
    )
}

export default PlaceList